import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getLanguageFromPath, buildLocalizedPath } from "@/lib/i18n";
import { useLanguage, type Language } from "@/contexts/LanguageContext";
import { LANGUAGE_SELECTION_EVENT, loadLanguage } from "@/lib/translations";
import { recoverChunkLoadError } from "@/lib/chunkRecovery";

const LANG_STORAGE_KEY = "fluxabee-lang";

const readStoredLang = (): Language | null => {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(LANG_STORAGE_KEY);
    if (v === "pl" || v === "de" || v === "en") return v;
  } catch { /* ignore */ }
  return null;
};

const persistLang = (lang: Language) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LANG_STORAGE_KEY, lang);
  } catch { /* ignore */ }
  try {
    const secure = window.location.protocol === "https:" ? "; secure" : "";
    document.cookie = `${LANG_STORAGE_KEY}=${lang}; path=/; max-age=31536000; samesite=lax${secure}`;
  } catch { /* ignore */ }
};

/**
 * Keeps the URL's language prefix in sync with the user's saved preference
 * (localStorage/cookie). Complements <LanguageUrlSync />, which treats URL as
 * authoritative - this guard enforces saved preference, redirecting when
 * the two diverge. Runs on every navigation so OAuth callbacks and external
 * navigates that drop the prefix get corrected.
 */
export const LanguagePersistenceGuard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language, setLanguageFromUrl } = useLanguage();

  useEffect(() => {
    // Auth-gated routes (/account/*) live only on top-level - redirecting
    // them through `buildLocalizedPath` would loop against the localized
    // `/pl/account` -> `/account` redirect in App.tsx. Instead, sync state
    // from the saved preference directly so translations work without
    // touching the URL. Mirrors the matching skip in `<LanguageUrlSync />`.
    if (/^\/account(\/|$)/.test(location.pathname)) {
      const stored = readStoredLang();
      if (!stored) {
        // No explicit preference - persist current language state as a sane
        // default so subsequent navigations stay consistent. Do NOT sniff
        // navigator.language: that breaks SEO (Googlebot is redirected away
        // from /pl/* and /de/* URLs since its navigator.language is en-US).
        persistLang(language);
        return;
      }
      if (stored !== language) setLanguageFromUrl(stored);
      return;
    }

    const urlLang: Language = getLanguageFromPath(location.pathname) ?? "en";
    const stored = readStoredLang();
    if (!stored) {
      // First visit: treat the URL as authoritative. Persist the URL's
      // language so future navigations are consistent, sync language state,
      // and crucially do NOT redirect - redirecting based on navigator.language
      // broke SEO for /pl/* and /de/* (Googlebot's navigator.language is en-US,
      // causing every localized URL to be flagged as a redirect and dropped
      // from the index).
      persistLang(urlLang);
      if (urlLang !== language) setLanguageFromUrl(urlLang);
      return;
    }
    if (stored === urlLang) return;
    const nextPath = buildLocalizedPath(location.pathname, stored);
    if (nextPath === location.pathname) return;
    let active = true;
    const cancelPreferenceRedirect = () => { active = false; };
    window.addEventListener(LANGUAGE_SELECTION_EVENT, cancelPreferenceRedirect);
    const destination = nextPath + location.search + location.hash;
    void loadLanguage(stored).then(() => {
      if (active) navigate(destination, { replace: true });
    }).catch((error: unknown) => {
      if (active) recoverChunkLoadError(error, destination);
    });
    return () => {
      active = false;
      window.removeEventListener(LANGUAGE_SELECTION_EVENT, cancelPreferenceRedirect);
    };
  }, [location.pathname, location.search, location.hash, navigate, language, setLanguageFromUrl]);

  return null;
};

export default LanguagePersistenceGuard;
